<script setup>
import { computed } from 'vue';
import Auth from 'dashboard/api/auth';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import Avatar from 'next/avatar/Avatar.vue';
import SidebarProfileMenuStatus from './SidebarProfileMenuStatus.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSeparator,
  DropdownItem,
} from 'next/dropdown-menu/base';
import CustomBrandPolicyWrapper from '../../components/CustomBrandPolicyWrapper.vue';

defineProps({
  isCollapsed: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'openKeyShortcutModal']);

defineOptions({
  inheritAttrs: false,
});

const { t } = useI18n();

const currentUser = useMapGetter('getCurrentUser');
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const accountId = useMapGetter('getCurrentAccountId');
const globalConfig = useMapGetter('globalConfig/get');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const showChatSupport = computed(() => {
  return (
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.CONTACT_CHATWOOT_SUPPORT_TEAM
    ) && globalConfig.value.chatwootInboxToken
  );
});

const toggleChatSupport = () => {
  if (window.$chatwoot) {
    window.$chatwoot.toggle();
  }
};

const menuItems = computed(() => {
  return [
    {
      show: showChatSupport.value,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.CONTACT_SUPPORT'),
      icon: 'i-lucide-life-buoy',
      click: toggleChatSupport,
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.KEYBOARD_SHORTCUTS'),
      icon: 'i-lucide-keyboard',
      click: () => {
        emit('openKeyShortcutModal');
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.PROFILE_SETTINGS'),
      icon: 'i-lucide-user-pen',
      link: { name: 'profile_settings_index' },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.APPEARANCE'),
      icon: 'i-lucide-palette',
      click: () => {
        const ninja = document.querySelector('ninja-keys');
        ninja.open({ parent: 'appearance_settings' });
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.DOCS'),
      icon: 'i-lucide-book',
      link: 'https://www.chatwoot.com/hc/user-guide/en',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: true,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.CHANGELOG'),
      icon: 'i-lucide-scroll-text',
      link: 'https://www.chatwoot.com/changelog/',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: currentUser.value.type === 'SuperAdmin',
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.SUPER_ADMIN_CONSOLE'),
      icon: 'i-lucide-castle',
      link: '/super_admin',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.LOGOUT'),
      icon: 'i-lucide-power',
      click: Auth.logout,
    },
  ];
});

const allowedMenuItems = computed(() => {
  return menuItems.value.filter(item => item.show);
});
</script>

<template>
  <DropdownContainer
    class="relative min-w-0"
    :class="isCollapsed ? 'w-auto' : 'w-full'"
    @close="emit('close')"
  >
    <template #trigger="{ toggle, isOpen }">
      <button
        class="flex cursor-pointer items-center gap-2 rounded-lg p-1 text-left text-auth-muted transition-colors hover:bg-auth-soft hover:text-auth-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-auth-action dark:text-auth-muted-dark dark:hover:bg-white/10 dark:hover:text-auth-text-dark dark:focus-visible:outline-auth-accent"
        :class="[
          {
            'bg-auth-soft text-auth-text dark:bg-white/10 dark:text-auth-text-dark':
              isOpen,
          },
          isCollapsed ? 'justify-center' : 'w-full',
        ]"
        :title="isCollapsed ? currentUser.available_name : undefined"
        @click="toggle"
      >
        <Avatar
          :size="32"
          :name="currentUser.available_name"
          :src="currentUser.avatar_url"
          :status="currentUserAvailability"
          class="flex-shrink-0"
        />
        <div v-if="!isCollapsed" class="min-w-0">
          <div
            class="truncate text-sm font-medium leading-4 text-auth-text dark:text-auth-text-dark"
          >
            {{ currentUser.available_name }}
          </div>
          <div
            class="truncate text-xs text-auth-muted dark:text-auth-muted-dark"
          >
            {{ currentUser.email }}
          </div>
        </div>
      </button>
    </template>
    <DropdownBody
      class="bottom-12 z-50 mb-2 w-80 ltr:left-0 rtl:right-0 [&>.n-dropdown-body]:!gap-1 [&>.n-dropdown-body]:!rounded-xl [&>.n-dropdown-body]:!border-auth-border [&>.n-dropdown-body]:!bg-auth-surface [&>.n-dropdown-body]:!px-2 [&>.n-dropdown-body]:!py-2 [&>.n-dropdown-body]:!text-auth-text [&>.n-dropdown-body]:!shadow-xl [&>.n-dropdown-body]:!shadow-auth-brand/10 [&>.n-dropdown-body]:!backdrop-blur-none dark:[&>.n-dropdown-body]:!border-auth-border-dark dark:[&>.n-dropdown-body]:!bg-auth-surface-dark dark:[&>.n-dropdown-body]:!text-auth-text-dark dark:[&>.n-dropdown-body]:!shadow-black/30"
    >
      <SidebarProfileMenuStatus />
      <DropdownSeparator
        class="!mx-0 !border-auth-border dark:!border-white/10"
      />
      <template v-for="item in allowedMenuItems" :key="item.label">
        <CustomBrandPolicyWrapper
          :show-on-custom-branded-instance="item.showOnCustomBrandedInstance"
        >
          <DropdownItem
            v-if="item.show"
            v-bind="item"
            class="!rounded-lg !px-3 !py-2.5 !text-auth-text transition-colors hover:!bg-auth-soft focus-visible:!outline focus-visible:!outline-2 focus-visible:!outline-offset-0 focus-visible:!outline-auth-action dark:!text-auth-text-dark dark:hover:!bg-white/[0.07] dark:focus-visible:!outline-auth-accent [&>svg]:!text-auth-muted dark:[&>svg]:!text-auth-muted-dark"
          />
        </CustomBrandPolicyWrapper>
      </template>
    </DropdownBody>
  </DropdownContainer>
</template>
